import { observer } from 'mobx-react-lite'
import { useState, type FC } from 'react'
import { EmptyState, MenuTitle } from 'shared/ui'
import { DragAndDropSection } from 'shared/ui/DragAndDrop'
import { type Segment } from 'entities/Segment/model/Segment'
import { SegmentItem } from '../SegmentItem/SegmentItem'

import styles from './styles.module.scss'

type ISegmentMenuItemProps = {
  segments: Segment[]
  search: string
  setSearch: (value: string) => void
  placeholder: string
  title: string
  droppableId?: string
  onReorder?: (orderedIds: string[]) => void
}

export const SegmentMenuItem: FC<ISegmentMenuItemProps> = observer(
  ({ segments, search, setSearch, placeholder, title, droppableId, onReorder }) => {
    const [isOpen, setIsOpen] = useState<boolean>(true)

    const handleToggle = () => {
      setIsOpen(!isOpen)
    }

    const renderEmptyState = () => {
      return (
        <div className={styles.empty}>
          <EmptyState
            noIcon
            alignItems={'flex-start'}
            typographyProps={{
              variant: 'body-md-regular',
              color: 'var(--content-primary-disabled)',
              ariaLabel: 'EmptyState',
            }}
          />
        </div>
      )
    }

    const renderSegments = () => {
      if (onReorder && droppableId && segments.length > 1) {
        return (
          <DragAndDropSection
            droppableId={droppableId}
            disableInteractiveElementBlocking
            items={segments.map((segment) => ({ key: String(segment.id), segment }))}
            onReorder={(orderedItems) => {
              onReorder(orderedItems.map((item) => item.key))
            }}
            renderItem={(item, provided) => (
              <SegmentItem segment={item.segment} dragHandleProps={provided.dragHandleProps} />
            )}
          />
        )
      }

      return segments.map((segment) => <SegmentItem key={segment.id} segment={segment} />)
    }

    return (
      <>
        {(!!segments.length || search) && (
          <MenuTitle
            useSearch={{
              handleSearch: setSearch,
              search: search,
              placeholder: placeholder,
              autofocus: true,
              variant: 'transparent',
            }}
            title={title}
            count={0}
            open={isOpen}
            handleToggle={handleToggle}
          />
        )}
        {Boolean(!segments.length && search.length) && renderEmptyState()}
        {isOpen && <div className={styles.segments}>{renderSegments()}</div>}
      </>
    )
  }
)
